import { type CallLogEntry } from '@/hooks/useCallLog';
import { PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail } from 'lucide-react';

interface RecentCallsListProps {
  calls: CallLogEntry[];
  onRedial: (number: string, meta?: { contactId?: string; leadId?: string; accountId?: string }) => void;
}

function getCallIcon(entry: CallLogEntry) {
  if (entry.voicemail_url) return <Voicemail className="h-3.5 w-3.5 text-amber-500" />;
  if (entry.direction === 'inbound' && entry.status !== 'completed') return <PhoneMissed className="h-3.5 w-3.5 text-red-500" />;
  if (entry.direction === 'inbound') return <PhoneIncoming className="h-3.5 w-3.5 text-blue-500" />;
  return <PhoneOutgoing className="h-3.5 w-3.5 text-green-500" />;
}

function getContactName(entry: CallLogEntry): string {
  if (entry.contact) {
    return [entry.contact.first_name, entry.contact.last_name].filter(Boolean).join(' ');
  }
  if (entry.lead?.name) return entry.lead.name;
  return entry.direction === 'outbound' ? entry.to_number : entry.from_number;
}

function getRemoteNumber(entry: CallLogEntry): string {
  return entry.direction === 'outbound' ? entry.to_number : entry.from_number;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function RecentCallsList({ calls, onRedial }: RecentCallsListProps) {
  if (calls.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No recent calls
      </div>
    );
  }

  return (
    <div className="max-h-80 overflow-y-auto">
      {calls.map(entry => {
        const name = getContactName(entry);
        const number = getRemoteNumber(entry);
        const isNamedContact = name !== number;

        return (
          <button
            key={entry.id}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted transition-colors"
            onClick={() => onRedial(number, {
              contactId: entry.contact_id || undefined,
              leadId: entry.lead_id || undefined,
              accountId: entry.account_id || undefined,
            })}
          >
            {getCallIcon(entry)}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{name}</p>
              {isNamedContact && (
                <p className="text-xs text-muted-foreground font-mono">{number}</p>
              )}
            </div>
            <span className="text-xs text-muted-foreground shrink-0">
              {timeAgo(entry.created_at)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
